import type { BirthDetails } from '../types/kundali';

const BTR_PROVENANCE_FIELDS = [
  'isRectified',
  'rectifiedDeltaSeconds',
  'originalTime',
  'rectifiedAt',
  'rectifiedTime',
  'rectifiedInterval',
  'workingTime',
  'confirmedTime',
  'btrStatus',
  'btrResponses',
  'rectificationNotes',
  'rectificationEvents',
  'btrTimeSource',
  'btrInitialUncertainty',
  'btrAuditResult'
] as const satisfies readonly (keyof BirthDetails)[];

function hasBtrHistory(details: BirthDetails): boolean {
  return Boolean(
    details.isRectified ||
    (details.originalTime && details.originalTime.length > 0) ||
    (details.workingTime && details.workingTime.length > 0) ||
    (details.rectifiedTime && details.rectifiedTime.length > 0) ||
    (details.rectifiedInterval && details.rectifiedInterval.length > 0) ||
    (details.confirmedTime && details.confirmedTime.length > 0) ||
    (details.rectifiedAt && details.rectifiedAt.length > 0) ||
    (details.rectifiedDeltaSeconds && details.rectifiedDeltaSeconds !== 0) ||
    (details.btrStatus && details.btrStatus !== 'NOT_STARTED') ||
    (details.btrResponses && Object.keys(details.btrResponses).length > 0) ||
    (details.rectificationEvents && details.rectificationEvents.length > 0) ||
    (details.rectificationNotes && details.rectificationNotes.length > 0) ||
    (details.btrTimeSource && details.btrTimeSource.length > 0) ||
    (details.btrInitialUncertainty && details.btrInitialUncertainty.length > 0) ||
    (details.btrAuditResult !== undefined && details.btrAuditResult !== null)
  );
}

/**
 * Merge editable birth inputs with the existing BTR audit record.
 *
 * Historical values are retained verbatim. If date/time changed after BTR
 * evidence exists, the result is marked stale and isRectified is disabled;
 * historical candidate/confirmation fields are retained for audit only.
 * Missing provenance is never synthesized.
 */
export function mergeBtrProvenanceForBirthEdit(
  submitted: BirthDetails,
  existing: BirthDetails
): BirthDetails {
  const merged: BirthDetails = { ...submitted };

  for (const field of BTR_PROVENANCE_FIELDS) {
    const priorValue = existing[field];
    if (priorValue !== undefined) {
      (merged as unknown as Record<string, unknown>)[field] = priorValue;
    }
  }

  const birthDateChanged = submitted.date !== existing.date;
  const birthTimeChanged = submitted.time !== existing.time;

  if ((birthDateChanged || birthTimeChanged) && hasBtrHistory(existing)) {
    merged.isRectified = false;
    merged.btrStatus = 'STALE';
  }

  return merged;
}
